import { useEffect, useState } from 'react';
import { DEVICE_NAME_MAX, deriveApSsid, ssidSlug } from '@glyph/protocol-ts';
import { useRenameDevice } from '@glyph/core';
import { Dialog, Field, Note, TextInput, useToast } from '@glyph/ui';

export interface RenameDeviceDialogProps {
  open: boolean;
  onClose: () => void;
  /** Cloud device id; undefined on the local shell ("self"). */
  deviceId?: string;
  currentName: string;
  /** For the SSID preview: the unit's serial (cloud rows carry it) or its current SSID (local shell). */
  serial?: string | null;
  currentSsid?: string | null;
}

const serial4Of = (serial?: string | null, ssid?: string | null): string => {
  if (serial && serial.length >= 4) return serial.slice(-4).toUpperCase();
  const m = /-([A-Za-z0-9]{1,8})$/.exec(ssid ?? '');
  return m ? m[1]! : '····';
};

/**
 * Rename a unit (operator+). One free-form name everywhere (fleet, OLED, mDNS); the hotspot SSID is its slug,
 * so the dialog shows the exact SSID before anything is sent. The hotspot restarts with the new SSID.
 */
export function RenameDeviceDialog({ open, onClose, deviceId, currentName, serial, currentSsid }: RenameDeviceDialogProps) {
  const m = useRenameDevice(deviceId);
  const toast = useToast();
  const [value, setValue] = useState(currentName);
  useEffect(() => {
    if (open) setValue(currentName);
  }, [open, currentName]);
  const name = value.replace(/\s+/g, ' ').trim();
  const valid = name.length > 0 && name.length <= DEVICE_NAME_MAX && ssidSlug(name, '') !== '';
  const ssid = valid ? deriveApSsid(name, 'auto', serial4Of(serial, currentSsid)) : '—';
  const submit = async () => {
    if (!valid || name === currentName) return;
    try {
      await m.rename(name);
      toast.push(`Renamed to ${name} · hotspot ${ssid}`, 'ok');
      onClose();
    } catch (e) {
      toast.push((e as Error).message || 'Rename failed', 'bad');
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title="Rename unit" meta={`SSID ${ssid}`} confirm={{ label: 'Rename', onClick: submit, disabled: !valid || name === currentName, busy: m.isPending }}>
      <div className="flex flex-col gap-3">
        <Field label={`Name · up to ${DEVICE_NAME_MAX} characters`}>
          <TextInput
            block
            autoFocus
            value={value}
            maxLength={DEVICE_NAME_MAX}
            placeholder="Stage Left"
            aria-label="Unit name"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
        </Field>
        {!valid && name.length > 0 && <Note tone="warn">The name needs at least one letter or digit.</Note>}
        <Note tone="warn">The hotspot restarts as the new SSID, so anyone connected to it rejoins. Ethernet, cloud control and captions are not affected.</Note>
      </div>
    </Dialog>
  );
}
